import {createRouter, createWebHistory} from 'vue-router';
import Home from '@/views/Home.vue';
import Characters from '@/views/Characters.vue';
import Community from '@/views/Community.vue';
import FAQ from '@/views/FAQ.vue';
import ArtGallery from "@/views/ArtGallery.vue";
import PlayerCheatSheet from "@/views/PlayerCheatSheet.vue";
import WatIsIt from "@/views/WatIsIt.vue";
import NotFound from "@/views/NotFound.vue";
import store from "@/store/index.js";
import {safeRedirectTarget} from "@/utils/redirect.js";
import {createTrainingGuard} from "@/router/trainingGuard.js";
import i18n from "@/i18n";
import Login from "@/views/Login.vue";
import CharacterSubmit from "@/views/Player/CharacterSubmit.vue";
import CharacterView from "@/views/Cartograph/Map.vue";
import LocationView from "@/views/Game/Location.vue";
import Register from "@/views/Register.vue";
import GameMasterMain from "@/views/GameMaster/GameMasterMain.vue";
import GameMasterLogin from "@/views/GameMaster/GameMasterLogin.vue"; // Component handles impersonation now
import CharacterSkills from "@/views/CharacterSkills.vue";
import CharacterInfo from "@/views/CharacterInfo.vue";
import MiniMap from "@/views/Cartograph/MiniMap.vue";
import UserMap from "@/views/Cartograph/UserMap.vue";
import WorldEditor from "@/views/WorldEditor/WorldEditor.vue";
import CharacterTemplateEditor from "@/views/GameMaster/CharacterTemplateEditor.vue";
import PlayerHomeDashboard from "@/views/PlyerHomeDashboard/PlayerHomeDashboard.vue";
import StoryBuilder from "@/views/StoryBuilder/StoryBuilder.vue";
import TabletopMapEditor from "@/views/GameMaster/TabletopMapEditor.vue";
import TabletopMapPlayer from "@/views/GameMaster/TabletopMapPlayer.vue";
import SkillCalculator from "@/views/GameMaster/SkillCalculator.vue";
import SkillImpactVisualization from "@/views/GameMaster/SkillImpactVisualization.vue";
import ArchetypeComparison from "@/views/GameMaster/ArchetypeComparison.vue";
import {findBySlug} from "@/registry/devComponents.js";

const SITE_NAME = 'Dimension-X';

const routes = [
    // Public pages
    {path: '/', name: 'Home', component: Home, meta: {requiresAuth: false, title: 'navigation.home', redirectWhenAuthenticated: true, viewableWhenAuthenticated: true}},
    {path: '/community', name: 'Community', component: Community, meta: {requiresAuth: false, title: 'community.title'}},
    {path: '/faq', name: 'FAQ', component: FAQ, meta: {requiresAuth: false, title: 'faq.title'}},
    {path: '/art-gallery', name: 'ArtGallery', component: ArtGallery, meta: {requiresAuth: false, title: 'gallery.title'}},
    {path: '/faq/player-cheatsheet', name: 'PlayerCheatSheet', component: PlayerCheatSheet, meta: {requiresAuth: false, title: 'playerCheatSheet.title'}},
    {path: '/faq/what-is-it', name: 'WhatIsIt', component: WatIsIt, meta: {requiresAuth: false, title: 'whatIsIt.title'}},

    // The Newcomers Guide was merged into the Start Here page. The old URL is
    // kept so existing links and search results do not 404.
    {path: '/faq/newcomers-guide', redirect: {name: 'WhatIsIt'}},

    // Legal. Both views existed but had no route and no link anywhere, which is
    // a compliance gap for Google OAuth consent and app-store review.
    {path: '/privacy-policy', name: 'PrivacyPolicy', component: () => import('@/views/PrivacyPolicy.vue'), meta: {requiresAuth: false, title: 'legal.privacyTitle'}},
    {path: '/account-deletion', name: 'AccountDeletion', component: () => import('@/views/AccountDeletion.vue'), meta: {requiresAuth: true, title: 'legal.deletionTitle'}},

    // Authentication routes
    {path: '/login', name: 'Login', component: Login, meta: {requiresAuth: false, title: 'auth.login', redirectWhenAuthenticated: true}},
    {path: '/register', name: 'Register', component: Register, meta: {requiresAuth: false, title: 'auth.register', redirectWhenAuthenticated: true}},
    /*
     * The optional segment carries the token from the email link; without it the
     * page asks for the code by hand. No redirectWhenAuthenticated: bouncing an
     * already-signed-in visitor would abandon a link that is still worth
     * redeeming, and the page signs them in itself on success.
     */
    {path: '/activate/:token?', name: 'Activate', component: () => import('@/views/Activate.vue'), meta: {requiresAuth: false, title: 'auth.activation.title', noIndex: true}},

    // Player dashboard and character management
    {path: '/player/dashboard', name: 'PlayerHomeDashboard', component: PlayerHomeDashboard, meta: {requiresAuth: true, game: true, title: 'player.lobby.title', playerShell: true}},
    {path: '/player/characters', name: 'Characters', component: Characters, meta: {requiresAuth: true}},
    {path: '/player/characters/submit', name: 'CharacterSubmit', component: CharacterSubmit, meta: {requiresAuth: true, game: true, playerShell: true, title: 'player.creation.eyebrow'}},
    {path: '/player/characters/info', name: 'CharacterInfo', component: CharacterInfo, meta: {requiresAuth: true, game: true}},
    {path: '/player/characters/skills', name: 'CharacterSkills', component: CharacterSkills, meta: {requiresAuth: true}},

    {path: '/player/characters/:characterId', name: 'PlayerCharacterDetail', component: () => import('@/views/Player/CharacterDetail.vue'), meta: {requiresAuth: true, game: true, title: 'player.detail.title', playerShell: true}},
    {path: '/player/characters/:characterId/edit', name: 'PlayerCharacterEdit', component: () => import('@/views/Player/CharacterEditor.vue'), meta: {requiresAuth: true, game: true, title: 'player.editor.editTitle', playerShell: true}},
    {path: '/player/characters/:characterId/publish', name: 'PlayerCharacterPublish', component: () => import('@/views/Player/CharacterPublish.vue'), meta: {requiresAuth: true, game: true, title: 'player.publish.title', playerShell: true}},

    // Map and cartography routes
    {path: '/map', name: 'UserMap', component: UserMap, meta: {requiresAuth: true}},
    {path: '/map/mini', name: 'MiniMap', component: MiniMap, meta: {requiresAuth: true}},

    // One door for the four world-content pages, so the nav does not need
    // four top-level entries for what is really a single idea.
    {path: '/world', name: 'WorldHub', component: () => import('@/views/WorldHub.vue'), meta: {requiresAuth: false, title: 'world.title'}},

    // Public previews — these are what the main nav links to.
    {path: '/world-map/preview', name: "WorldMapPreview", component: () => import('@/views/WorldMapPreview.vue'), meta: {requiresAuth: false, title: 'worldMap.title', viewport: true}},
    {path: '/characters/preview', name: 'CharacterPreview', component: () => import('@/views/CharacterPreview.vue'), meta: {requiresAuth: false, title: 'characters.title'}},
    {path: '/characters/:publishedId', name: 'PublicCharacterProfile', component: () => import('@/views/PublicCharacterProfile.vue'), meta: {requiresAuth: false, title: 'characters.title'}},
    {path: '/books/preview', name: 'BooksPreview', component: () => import('@/views/BooksView.vue'), meta: {requiresAuth: false, title: 'books.title'}},

    /*
     * The dice chamber, open to visitors: one d20, no cards, no session. The
     * old internal path stays as an alias so existing links keep working.
     */
    {path: '/roll20', alias: '/dice/lab', name: 'DiceLab', component: () => import('@/views/DiceLab.vue'), meta: {requiresAuth: false, title: 'navigation.roll20'}},

    // Old internal-tool paths, superseded by the /dev gallery. Kept as
    // redirects so existing links and bookmarks do not 404.
    ...[
        ['/dev/fight-stage', 'fight-stage'],
        ['/dev/character-card', 'character-card'],
        ['/dev/duel', 'duel-bench'],
        ['/dev/impact-lab', 'impact-lab'],
    ].map(([path, slug]) => ({path, redirect: {name: 'DevComponent', params: {slug}}})),

    // Paths whose harness was retired. The gallery index is the honest
    // destination; the components behind them no longer exist.
    ...['/dice', '/dice/roller', '/preview/character/token', '/timeline']
        .map(path => ({path, redirect: {name: 'DevGallery'}})),

    // Game
    {path: '/game', name: 'Game', component: LocationView, meta: {requiresAuth: true, game: true}},
    /*
     * Training is a route of its own, never a mode of `/game`: the two resolve
     * different characters at the same time, so the real character stays in the
     * world and playable while a training is open. `character` names the real
     * character whose training to open; without it the client's current
     * character is assumed.
     */
    {path: '/game/training', name: 'GameTraining', component: LocationView, meta: {requiresAuth: true, game: true, training: true, title: 'fightHud.trainingTitle', noIndex: true}},

    // Game Master routes
    {path: '/game-master', name: 'GameMaster', component: GameMasterMain, meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    {path: '/game-master/impersonate', name: 'GameMasterImpersonate', component: GameMasterLogin, meta: {requiresAuth: false, game: true}},
    {path: '/game-master/cartograph', name: 'Cartograph', component: CharacterView, meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    {path: '/game-master/world-editor', name: 'WorldEditor', component: WorldEditor, meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    {path: '/game-master/character-template-editor', name: 'CharacterTemplateEditor', component: CharacterTemplateEditor, meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    {path: '/game-master/story-builder', name: 'StoryBuilder', component: () => import('@/views/GmJournalScreen.vue'), meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    {path: '/game-master/tabletop-map-editor', name: 'TabletopMapEditor', component: TabletopMapEditor, meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    {path: '/game-master/tabletop-map-player', name: 'TabletopMapPlayer', component: TabletopMapPlayer, meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    {path: '/game-master/compendium', name: 'Compendium', component: () => import('@/views/GameMaster/Compendium.vue'), meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    {path: '/game-master/skill-calculator', name: 'SkillWorkbench', component: () => import('@/views/GameMaster/SkillWorkbench.vue'), meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    /* The progression chart the workbench replaced. Kept reachable because it
       answers a different question — how one skill scales across a whole stat
       range — which the workbench, resolving one stat line at a time, does not. */
    {path: '/game-master/skill-progression', name: 'SkillCalculator', component: SkillCalculator, meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    {path: '/game-master/skill-impact-visualization', name: 'SkillImpactVisualization', component: SkillImpactVisualization, meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    {path: '/game-master/archetype-comparison', name: 'ArchetypeComparison', component: ArchetypeComparison, meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    {path: '/game-master/world-map', name: "WorldMapGameMaster", component: () => import('@/views/WorldMapGameMaster.vue'), meta: {requiresAuth: true, requiresGameMaster: true, game: true}},
    {path: '/game-master/campaign-builder', name: 'CampaignBuilder', component: () => import('@/views/GameMaster/CampaignBuilder/CampaignBuilderPipeline.vue'), meta: {requiresAuth: true, requiresGameMaster: true, game: true}},

    // The /dev component gallery, generated from the registry so a new entry
    // never needs a hand-written route.
    {
        path: '/dev',
        name: 'DevGallery',
        component: () => import('@/views/Dev/DevGallery.vue'),
        meta: {requiresAuth: false, noIndex: true, title: 'dev.title'},
    },
    {
        path: '/dev/component/:slug',
        name: 'DevComponent',
        component: () => import('@/views/Dev/DevComponentHost.vue'),
        meta: {requiresAuth: false, noIndex: true, bare: true},
        beforeEnter(to) {
            const entry = findBySlug(to.params.slug);
            if (entry?.notReady) {
                return {name: 'DevGallery'};
            }
            if (entry?.requiresAuth && !store.getters.isAuthenticated) {
                return {name: 'Login', query: {redirect: to.fullPath}};
            }
            to.meta.title = entry?.titleKey;
        },
    },

    // 404 page - catch all route
    {path: '/:pathMatch(.*)*', name: 'NotFound', component: NotFound, meta: {requiresAuth: false, title: 'notFound.title'}},
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    /*
     * There was no scrollBehavior at all, so following a link from halfway down
     * one page landed halfway down the next one.
     */
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) return savedPosition;
        if (to.hash) return {el: to.hash, behavior: 'smooth', top: 16};
        if (to.path === from.path) return false;
        return {top: 0};
    }
});

const LOBBY_ROUTE = {name: 'PlayerHomeDashboard'};

router.beforeEach((to, from, next) => {
    const authenticated = store.getters.isAuthenticated;

    /*
     * A token that decoded but expired leaves the cookie in place while every
     * getter reports a guest, so the header and the guard silently disagree
     * with document.cookie. Purge it once, here.
     */
    if (!authenticated && store.getters.getAuthToken) {
        store.dispatch('logout');
    }

    if (to.matched.some(record => record.meta.requiresAuth) && !authenticated) {
        // Remember where they were headed so login can return them there.
        next({name: 'Login', query: {redirect: to.fullPath}});
        return;
    }

    /*
     * A UX guard, not a security boundary: the claim is client-decodable and
     * the cookie is JS-writable, so this only keeps non-GMs off a screen that
     * would render broken chrome and then 403. The server-side check is the
     * actual control.
     */
    if (to.matched.some(record => record.meta.requiresGameMaster)) {
        if (!authenticated) {
            next({name: 'Login', query: {redirect: to.fullPath}});
            return;
        }
        if (!store.getters.isGameMaster) {
            next({...LOBBY_ROUTE, replace: true});
            return;
        }
    }

    /*
     * Arriving from outside the app — a bookmark, a typed URL, the tail of the
     * login flow — a signed-in player wants the lobby, not the sales pitch.
     * Following a link from inside the app is the opposite: it is a deliberate
     * request to see that page. `from.name` is undefined only on the first
     * navigation of a page load, which is exactly the difference. Without this
     * the brand mark in the player shell bounced straight back to the lobby,
     * and the public site had no door at all once you signed in.
     *
     * Only the landing page opts in. /login and /register stay unconditional:
     * there is nothing there for someone already signed in.
     */
    const staysOpen = to.meta.viewableWhenAuthenticated && from.name !== undefined;
    if (authenticated && to.meta.redirectWhenAuthenticated && !staysOpen) {
        const target = safeRedirectTarget(to.query.redirect);
        next(target ? {path: target, replace: true} : {...LOBBY_ROUTE, replace: true});
        return;
    }

    next();
});

router.beforeEach(createTrainingGuard(LOBBY_ROUTE));

router.afterEach((to) => {
    /*
     * Every route used to share the one <title> from index.html, so tabs and
     * browser history were indistinguishable. The key is resolved through i18n
     * so the title follows the active locale.
     */
    const key = to.meta.title;
    const translated = key && i18n.global.te(key) ? i18n.global.t(key) : '';
    document.title = translated ? `${translated} — ${SITE_NAME}` : `${SITE_NAME} | Turn-Based Online D&D Game`;

    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
    }
    robots.setAttribute('content', to.meta.noIndex ? 'noindex, nofollow' : 'index, follow');
});

export default router;
